import { useI18n } from 'vue-i18n';
import type { LocalizedString } from '@lingvo-news/contracts';

/**
 * Localizes a LocalizedString object using Vue i18n
 * @param localizedString - The LocalizedString to localize
 * @returns The localized string with format parameters applied
 */
export function localize(localizedString: LocalizedString): string {
	const { t } = useI18n();

	const fixedValue = (() => {
		const v: unknown = localizedString;
		if (!v || typeof v !== 'object') return undefined;
		if (!('fixedValue' in v)) return undefined;
		const fv = (v as { readonly fixedValue?: unknown }).fixedValue;
		return typeof fv === 'string' ? fv : undefined;
	})();

	const stringId = localizedString.stringId;
	if (!stringId) return fixedValue ?? '';

	const translated = t(stringId, localizedString.formatParams);
	const translatedText = typeof translated === 'string' ? translated : String(translated);

	// Prefer i18n for real stringIds; fall back to fixedValue only when missing.
	if (fixedValue) {
		if (localizedString.stringId === 'common.rawText') return fixedValue;
		if (translatedText === localizedString.stringId) return fixedValue;
	}

	return translatedText;
}

/**
 * Localizes a LocalizedString object or returns the string as-is if it's already a string
 * Useful for backward compatibility during migration
 * @param value - The LocalizedString to localize or a plain string
 * @returns The localized string or the original string
 */
export function localizeOrString(value: LocalizedString | string): string {
	if (typeof value === 'string') {
		return value;
	}

	return localize(value);
}
